import {
  createContext,
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type Registration = { enabled: boolean; refresh: () => Promise<void> | void };
type PullRefreshContextValue = {
  register: (registration: Registration | null) => void;
};

const PullRefreshContext = createContext<PullRefreshContextValue | null>(null);

export const PullRefreshProvider = ({
  children,
  scrollContainerRef,
}: PropsWithChildren<{ scrollContainerRef: RefObject<HTMLElement> }>) => {
  const registrationRef = useRef<Registration | null>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const startY = useRef<number | null>(null);
  const distanceRef = useRef(0);
  const eligibleGestureRef = useRef(false);
  const register = useCallback((value: Registration | null) => {
    registrationRef.current = value;
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const onTouchStart = (event: TouchEvent) => {
      startY.current = null;
      distanceRef.current = 0;
      setPullDistance(0);
      eligibleGestureRef.current = false;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        !registrationRef.current?.enabled ||
        refreshingRef.current ||
        container.scrollTop !== 0 ||
        target.closest("input, textarea, select, [contenteditable='true'], .ant-drawer, .ant-modal")
      ) return;
      startY.current = event.touches[0]?.clientY ?? null;
      eligibleGestureRef.current = startY.current !== null;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!eligibleGestureRef.current || startY.current === null || refreshingRef.current) return;
      const distance = event.touches[0].clientY - startY.current;
      if (distance <= 0) {
        eligibleGestureRef.current = false;
        setPullDistance(0);
        return;
      }
      distanceRef.current = Math.min(distance * 0.5, 96);
      setPullDistance(distanceRef.current);
      if (distance > 8 && event.cancelable) event.preventDefault();
    };

    const onTouchEnd = async () => {
      const shouldRefresh = eligibleGestureRef.current && distanceRef.current >= 36 && registrationRef.current?.enabled;
      startY.current = null;
      eligibleGestureRef.current = false;
      if (!shouldRefresh) {
        distanceRef.current = 0;
        setPullDistance(0);
        return;
      }
      refreshingRef.current = true;
      setRefreshing(true);
      try {
        await registrationRef.current?.refresh();
      } catch {
        // The page loader is responsible for reporting request errors.
      } finally {
        refreshingRef.current = false;
        distanceRef.current = 0;
        setRefreshing(false);
        setPullDistance(0);
      }
    };

    const onTouchCancel = () => {
      startY.current = null;
      eligibleGestureRef.current = false;
      distanceRef.current = 0;
      setPullDistance(0);
    };

    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);
    container.addEventListener("touchcancel", onTouchCancel);
    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [scrollContainerRef]);

  return (
    <PullRefreshContext.Provider value={{ register }}>
      {pullDistance > 0 || refreshing ? (
        <div className="pull-refresh-indicator" aria-live="polite">
          {refreshing ? "⟳ 更新中…" : pullDistance >= 36 ? "↓ 離して更新" : "↓ 引いて更新"}
        </div>
      ) : null}
      <div style={{ transform: `translateY(${refreshing ? 36 : pullDistance}px)` }}>
        {children}
      </div>
    </PullRefreshContext.Provider>
  );
};

export const usePageRefresh = () => {
  const context = useContext(PullRefreshContext);
  if (!context) throw new Error("usePageRefresh must be used inside PullRefreshProvider");
  return context.register;
};
